import { ModalConfirmation, ModalProps, notify, useModal } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { queryClient } from '../../../../../../config/queryClient'
import { NotificationKeys } from '../../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../../enums/queryKeys'
import { APIError } from '../../../../../../utils'
import { useInvoice } from '../../../../hooks/useInvoice'
import { useVoidInvoice } from '../../hooks/useVoidInvoice'
import { ModalBody } from './elements/ModalBody'

interface VoidCreditNoteModalProps extends ModalProps {
  creditNoteId: string
  onSuccess?: () => void
}

export const VoidCreditNoteModal = ({ creditNoteId, id, onSuccess }: VoidCreditNoteModalProps): ReactElement => {
  const { t } = useTranslation()
  const { isOpen, close: closeVoidCreditNoteModal } = useModal(id)
  const { data: invoiceData } = useInvoice(isOpen ? creditNoteId : '')

  const { voidInvoice, isLoading: isProcessing } = useVoidInvoice({
    onSuccess: () => {
      notify({
        id: NotificationKeys.InvoiceVoided,
        message: t('invoices.void_document.success'),
        variant: 'success',
      })
      closeVoidCreditNoteModal()
      queryClient.invalidateQueries(QueryKeys.Invoices)
      queryClient.invalidateQueries(QueryKeys.Transactions)
      onSuccess?.()
    },
    onError: (error?: APIError) => {
      notify({
        id: NotificationKeys.InvoiceVoided,
        message: error?.message || t('error'),
        variant: 'error',
      })
    },
  })

  const handleVoidCreditNote = useCallback(() => {
    voidInvoice(creditNoteId)
  }, [voidInvoice, creditNoteId])

  return (
    <ModalConfirmation
      id={id}
      cancelLabel={t('cancel')}
      danger
      message={
        <ModalBody
          balanceModifiers={invoiceData?.invoice.balanceModifiers}
          currencyId={invoiceData?.invoice.currencyId}
        />
      }
      okLabel={t('invoices.void_credit_note_modal.confirm')}
      okLoading={isProcessing}
      onOk={handleVoidCreditNote}
      title={t('invoices.void_credit_note_modal.title')}
    />
  )
}
